import React, { useState, useEffect, useMemo } from 'react';
import {
    MessageSquare, BarChart2, CheckSquare, Send, Users,
    ChevronRight, X, AlertCircle, TrendingUp, Filter, ThumbsUp,
    Check, Circle
} from 'lucide-react';
import { slidesService } from '../../services/slidesService';
import { useAuth } from '../../contexts/AuthContext';
import PdfSlide from './PdfSlide';

const InteractiveSidebar = ({
    sessionId,
    isPresenter,
    currentSlideIndex,
    slideInteractions = [],
    slides = [],
    onSelectSlide,
    deckPdfUrl = null
}) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState(isPresenter ? 'slides' : 'polls');
    const [liveInteractions, setLiveInteractions] = useState([]);
    const [questionText, setQuestionText] = useState('');
    const [error, setError] = useState(null);
    const [userVotes, setUserVotes] = useState({}); // Track user's votes per interaction
    const [userAnswers, setUserAnswers] = useState({}); // Track user's MCQ answers

    // Subscribe to live session interactions if session exists
    useEffect(() => {
        if (!sessionId) return;

        const unsubscribe = slidesService.subscribeToInteractions(sessionId, (data) => {
            setLiveInteractions(data);
        });

        return () => unsubscribe?.();
    }, [sessionId]);

    // Combine slide interactions with live session interactions
    const allInteractions = [...slideInteractions, ...liveInteractions];

    // Filter by type
    const polls = allInteractions.filter(i =>
        i.type === 'poll' || (i.type === 'choice' && i.kind === 'poll')
    );
    const mcqs = allInteractions.filter(i =>
        i.type === 'mcq' || (i.type === 'choice' && i.kind === 'mcq')
    );
    const questions = liveInteractions
        .filter(i => i.type === 'question')
        .filter(i => i.slideIndex === currentSlideIndex);

    const slideItems = useMemo(() => {
        return slides.map((slide, index) => ({
            id: slide.id || `slide-${index}`,
            index
        }));
    }, [slides]);

    const handleSubmitQuestion = async () => {
        if (!questionText.trim()) return;
        setError(null);
        try {
            if (sessionId) {
                await slidesService.submitResponse(sessionId, 'questions_interaction', user.uid, {
                    type: 'question',
                    text: questionText,
                    slideIndex: currentSlideIndex,
                    userName: user.displayName || 'Anonymous Viewer'
                });
            }
            setQuestionText('');
        } catch (err) {
            setError(err.message);
            setTimeout(() => setError(null), 5000);
        }
    };

    const handlePollVote = async (interactionId, optionIndex, allowMultiple = false) => {
        setUserVotes(prev => {
            if (!allowMultiple) return { ...prev, [interactionId]: optionIndex };
            const current = Array.isArray(prev[interactionId]) ? prev[interactionId] : [];
            const next = current.includes(optionIndex)
                ? current.filter(idx => idx !== optionIndex)
                : [...current, optionIndex];
            return { ...prev, [interactionId]: next };
        });

        try {
            if (sessionId) {
                await slidesService.submitResponse(sessionId, interactionId, user.uid, {
                    type: 'poll_vote',
                    optionIndex,
                    selectedOptions: allowMultiple ? (userVotes[interactionId] || []).concat(optionIndex) : [optionIndex],
                    slideIndex: currentSlideIndex
                });
            }
        } catch (err) {
            console.error('Vote failed:', err);
        }
    };

    const handleMcqAnswer = async (interactionId, optionIndex, correctAnswer, allowMultiple = false) => {
        setUserAnswers(prev => {
            if (!allowMultiple) {
                return {
                    ...prev,
                    [interactionId]: {
                        selected: optionIndex,
                        isCorrect: optionIndex === correctAnswer
                    }
                };
            }
            const current = Array.isArray(prev[interactionId]?.selected)
                ? prev[interactionId].selected
                : [];
            const next = current.includes(optionIndex)
                ? current.filter(idx => idx !== optionIndex)
                : [...current, optionIndex];
            return {
                ...prev,
                [interactionId]: {
                    selected: next,
                    isCorrect: null
                }
            };
        });

        try {
            if (sessionId) {
                await slidesService.submitResponse(sessionId, interactionId, user.uid, {
                    type: 'mcq_answer',
                    optionIndex,
                    selectedOptions: allowMultiple ? (userAnswers[interactionId]?.selected || []).concat(optionIndex) : [optionIndex],
                    isCorrect: allowMultiple ? null : optionIndex === correctAnswer,
                    slideIndex: currentSlideIndex
                });
            }
        } catch (err) {
            console.error('Answer failed:', err);
        }
    };

    const renderPolls = () => (
        <div className="flex flex-col h-full overflow-y-auto p-4 space-y-6">
            {polls.length > 0 ? (
                polls.map((poll, idx) => {
                    const allowMultiple = poll.allowMultiple === true;
                    const votedOption = userVotes[poll.id];
                    const selectedOptions = allowMultiple
                        ? (Array.isArray(votedOption) ? votedOption : [])
                        : [votedOption];
                    const hasVoted = allowMultiple ? selectedOptions.length > 0 : votedOption !== undefined;

                    return (
                        <div key={poll.id || idx} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-bold text-slate-800">{poll.title}</h4>
                                <span className="text-[10px] bg-blue-500 text-white px-2 py-1 rounded-full uppercase font-bold">
                                    Poll
                                </span>
                            </div>
                            <div className="space-y-2">
                                {poll.options?.map((option, optIdx) => {
                                    const isSelected = selectedOptions.includes(optIdx);
                                    return (
                                        <button
                                            key={optIdx}
                                            onClick={() => handlePollVote(poll.id, optIdx, allowMultiple)}
                                            disabled={!allowMultiple && hasVoted}
                                            className={`w-full text-left p-3 rounded-xl border-2 transition-all text-sm font-medium flex items-center gap-3 ${isSelected
                                                    ? 'border-blue-500 bg-blue-500 text-white'
                                                    : hasVoted
                                                        ? 'border-slate-200 bg-white text-slate-400 cursor-not-allowed'
                                                        : 'border-slate-200 bg-white text-slate-600 hover:border-blue-400 hover:bg-blue-50'
                                                }`}
                                        >
                                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-white bg-white' : 'border-slate-300'
                                                }`}>
                                                {isSelected && <Check className="w-3 h-3 text-blue-500" />}
                                            </span>
                                            {option}
                                        </button>
                                    );
                                })}
                            </div>
                            {hasVoted && (
                                <p className="text-xs text-blue-600 mt-3 text-center font-medium">
                                    ✓ Your vote has been recorded
                                </p>
                            )}
                        </div>
                    );
                })
            ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
                    <BarChart2 className="h-12 w-12 opacity-30" />
                    <p className="text-sm font-medium">No polls on this slide</p>
                    <p className="text-xs text-slate-300">Polls will appear here when available</p>
                </div>
            )}
        </div>
    );

    const renderMcqs = () => (
        <div className="flex flex-col h-full overflow-y-auto p-4 space-y-6">
            {mcqs.length > 0 ? (
                mcqs.map((mcq, idx) => {
                    const allowMultiple = mcq.allowMultiple === true;
                    const answer = userAnswers[mcq.id];
                    const hasAnswered = answer !== undefined && (allowMultiple ? (answer.selected || []).length > 0 : answer.selected !== undefined);

                    return (
                        <div key={mcq.id || idx} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-bold text-slate-800">{mcq.title}</h4>
                                <span className="text-[10px] bg-green-500 text-white px-2 py-1 rounded-full uppercase font-bold">
                                    Quiz
                                </span>
                            </div>
                            <div className="space-y-2">
                                {mcq.options?.map((option, optIdx) => {
                                    const selectedList = allowMultiple
                                        ? (Array.isArray(answer?.selected) ? answer.selected : [])
                                        : [answer?.selected];
                                    const isSelected = selectedList.includes(optIdx);
                                    const isCorrect = mcq.correctAnswer === optIdx;
                                    const showResult = !allowMultiple && hasAnswered && (isSelected || isCorrect);

                                    return (
                                        <button
                                            key={optIdx}
                                            onClick={() => handleMcqAnswer(mcq.id, optIdx, mcq.correctAnswer, allowMultiple)}
                                            disabled={!allowMultiple && hasAnswered}
                                            className={`w-full text-left p-3 rounded-xl border-2 transition-all text-sm font-medium flex items-center gap-3 ${showResult && isCorrect
                                                    ? 'border-green-500 bg-green-500 text-white'
                                                    : showResult && isSelected && !isCorrect
                                                        ? 'border-red-500 bg-red-500 text-white'
                                                        : hasAnswered
                                                            ? 'border-slate-200 bg-white text-slate-400 cursor-not-allowed'
                                                            : 'border-slate-200 bg-white text-slate-600 hover:border-green-400 hover:bg-green-50'
                                                }`}
                                        >
                                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${showResult && isCorrect ? 'border-white bg-white' :
                                                    showResult && isSelected ? 'border-white bg-white' : 'border-slate-300'
                                                }`}>
                                                {showResult && isCorrect && <Check className="w-3 h-3 text-green-500" />}
                                                {showResult && isSelected && !isCorrect && <X className="w-3 h-3 text-red-500" />}
                                            </span>
                                            {option}
                                        </button>
                                    );
                                })}
                            </div>
                            {hasAnswered && !allowMultiple && (
                                <p className={`text-xs mt-3 text-center font-bold ${answer.isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                    {answer.isCorrect ? '🎉 Correct!' : '❌ Incorrect - The right answer is highlighted'}
                                </p>
                            )}
                        </div>
                    );
                })
            ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
                    <CheckSquare className="h-12 w-12 opacity-30" />
                    <p className="text-sm font-medium">No quizzes on this slide</p>
                    <p className="text-xs text-slate-300">Quiz questions will appear here</p>
                </div>
            )}
        </div>
    );

    const renderQuestions = () => {
        return (
            <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {questions.length > 0 ? (
                        questions.map((q, idx) => (
                            <div key={idx} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-purple-300 transition-all">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="font-bold text-xs text-purple-600">{q.userName}</span>
                                    <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                                        Slide {currentSlideIndex + 1}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-700 leading-relaxed">{q.text}</p>
                            </div>
                        ))
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
                            <MessageSquare className="h-12 w-12 opacity-30" />
                            <p className="text-sm font-medium">No questions yet</p>
                            <p className="text-xs text-slate-300">Questions for this slide appear here</p>
                        </div>
                    )}
                </div>

                {!isPresenter && (
                    <div className="p-4 border-t bg-slate-50">
                        {error && (
                            <div className="mb-3 p-2 bg-red-50 text-red-600 text-xs rounded-lg flex items-center gap-2">
                                <AlertCircle className="h-3 w-3" />
                                {error}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <textarea
                                value={questionText}
                                onChange={(e) => setQuestionText(e.target.value)}
                                placeholder="Ask a question..."
                                rows={2}
                                className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                            />
                            <button
                                onClick={handleSubmitQuestion}
                                className="p-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50"
                                disabled={!questionText.trim()}
                            >
                                <Send className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderSlidesList = () => (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {slideItems.length > 0 ? (
                slideItems.map((slide) => {
                    const isActive = slide.index === currentSlideIndex;
                    const slideData = slides[slide.index];
                    const isPdf = slideData?.type === 'pdf' && deckPdfUrl;
                    return (
                        <button
                            key={slide.id}
                            onClick={() => onSelectSlide?.(slide.index)}
                            className={`w-full text-left rounded-2xl border transition-all overflow-hidden ${isActive
                                    ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                                    : 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50'
                                }`}
                        >
                            <div className="relative aspect-video w-full bg-slate-100 flex items-center justify-center">
                                {isPdf ? (
                                    <PdfSlide
                                        pdfUrl={deckPdfUrl}
                                        pageNumber={slideData?.pageNumber || slide.index + 1}
                                        scale={0.5}
                                        className="w-full h-full"
                                    />
                                ) : (
                                    <img
                                        src={slideData?.url}
                                        alt={`Slide ${slide.index + 1}`}
                                        className="w-full h-full object-contain"
                                    />
                                )}
                                <div className="absolute left-3 bottom-3 w-7 h-7 rounded-full bg-black/70 text-white text-xs font-bold flex items-center justify-center">
                                    {slide.index + 1}
                                </div>
                            </div>
                        </button>
                    );
                })
            ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
                    <Filter className="h-12 w-12 opacity-30" />
                    <p className="text-sm font-medium">No slides loaded</p>
                    <p className="text-xs text-slate-300">Upload slides to see the list</p>
                </div>
            )}
            </div>
        </div>
    );

    // Count interactions for badges
    const pollCount = polls.length;
    const mcqCount = mcqs.length;
    const questionCount = questions.length;

    return (
        <div className="w-80 h-full bg-white border-l shadow-xl flex flex-col overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b">
                {isPresenter && (
                    <button
                        onClick={() => setActiveTab('slides')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold transition-all border-b-2 ${activeTab === 'slides'
                                ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                                : 'border-transparent text-slate-400 hover:text-slate-600'
                            }`}
                    >
                        <Filter className="h-4 w-4" />
                        Slides
                    </button>
                )}
                <button
                    onClick={() => setActiveTab('polls')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold transition-all border-b-2 ${activeTab === 'polls'
                            ? 'border-blue-600 text-blue-600 bg-blue-50'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                        }`}
                >
                    <BarChart2 className="h-4 w-4" />
                    Polls
                    {pollCount > 0 && (
                        <span className="bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px]">
                            {pollCount}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('mcq')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold transition-all border-b-2 ${activeTab === 'mcq'
                            ? 'border-green-600 text-green-600 bg-green-50'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                        }`}
                >
                    <CheckSquare className="h-4 w-4" />
                    Quiz
                    {mcqCount > 0 && (
                        <span className="bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px]">
                            {mcqCount}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('questions')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold transition-all border-b-2 ${activeTab === 'questions'
                            ? 'border-purple-600 text-purple-600 bg-purple-50'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                        }`}
                >
                    <MessageSquare className="h-4 w-4" />
                    Questions
                    {questionCount > 0 && (
                        <span className="bg-purple-500 text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px]">
                            {questionCount}
                        </span>
                    )}
                </button>
            </div>

            {/* Current Slide Indicator */}
            <div className="px-4 py-2 bg-slate-50 border-b flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    {activeTab === 'slides' ? 'Slides List' : `Slide ${currentSlideIndex + 1} Interactions`}
                </span>
                {sessionId && (
                    <div className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                        <span className="text-[10px] font-bold text-green-600 uppercase">Live</span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'slides' && isPresenter && renderSlidesList()}
                {activeTab === 'polls' && renderPolls()}
                {activeTab === 'mcq' && renderMcqs()}
                {activeTab === 'questions' && renderQuestions()}
            </div>
        </div>
    );
};

export default InteractiveSidebar;
